import _ from 'lodash';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ChatTaskContext } from '../contexts/ChatTaskContext';
import { useInlineResponseTemplatingContext } from '../contexts/InlineResponseTemplatingContext';
import {
  ResponseTemplatePopperMetadata,
  ResponseTemplateStep,
  escapeModelId,
} from '../ResponseTemplateCreator/ResponseTemplateCreatorStep';
import { ResponseTemplatePath } from '../ResponseTemplateCreator/ResponseTemplatePath';
import { StepCard } from './StepCard';
import { Button } from '@scale/scaleui';
import { IndexedInteractionStepResponse } from '@scale/shared/types/chat';

type ResponseTemplatingButtonProps = {
  childId: string;
  metadata: ResponseTemplatePopperMetadata;
};

export const ResponseTemplatingButton: FC<ResponseTemplatingButtonProps> = ({
  childId,
  metadata,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const showResponseTemplatingIcon: boolean = !!taskContext.responseTemplatingMode;

  const { stepIndex, candidateId, fieldId, numberFieldScale } = metadata || {};

  const {
    stepsWithResponses,
    history,
    setResponseForIndex,
    updateResponseMiddleware,
    getVariables,
  } = useInlineResponseTemplatingContext();

  const [step, response] = stepsWithResponses[stepIndex ?? 0] ?? [];
  const templateVariables = getVariables();
  const templatePaths: ResponseTemplatePath[] = useMemo(() => {
    return history && step ? step.getResponseTemplatePaths(history.until(stepIndex ?? 0)) : [];
  }, [history, step, stepIndex]);

  const [stepTemplatedResponse, setStepTemplatedResponse] = useState<
    IndexedInteractionStepResponse | undefined
  >(response);

  useEffect(() => {
    if (!_.isEqual(response, stepTemplatedResponse)) {
      setStepTemplatedResponse(response);
    }
  }, [response, setStepTemplatedResponse, stepTemplatedResponse]);

  // Filter paths to only those that are relevant to this candidateId and/or fieldId
  const filteredPaths = useMemo(() => {
    let paths = templatePaths;
    if (candidateId) {
      // Filter if path.path includes candidateId, and it's wrapped by [" and "]
      paths = paths.filter(path => path.path.includes(escapeModelId(candidateId)));
    }
    if (fieldId) {
      // Filter if path.path includes candidateId, and it's surrounded by . and . (meaning it's an entire fieldId)
      paths = paths.filter(path => path.path.includes(`.${fieldId}.`));
    }
    return paths;
  }, [candidateId, fieldId, templatePaths]);

  const updateEditedResponse = useCallback(
    (path: string, value: any) => {
      if (!stepTemplatedResponse) return;
      const newStepResponse = _.cloneDeep(stepTemplatedResponse);
      _.set(newStepResponse, path, value);
      updateResponseMiddleware(newStepResponse, metadata, path, value);
      setStepTemplatedResponse(newStepResponse);
    },
    [metadata, stepTemplatedResponse, updateResponseMiddleware],
  );

  const saveTemplatedResponse = useCallback(() => {
    // Triggers upstream response change + population + propagation
    if (stepIndex !== undefined) {
      setResponseForIndex(stepTemplatedResponse!, stepIndex);
    }
  }, [setResponseForIndex, stepIndex, stepTemplatedResponse]);

  // Whether each path has a value
  const allMapped = useMemo(() => {
    return filteredPaths.every(path => {
      return !_.isNil(_.get(response, path.path)) && _.get(response, path.path) !== '';
    });
  }, [filteredPaths, response]);

  if (!showResponseTemplatingIcon) return null;

  if (
    !childId ||
    stepIndex === undefined ||
    !stepTemplatedResponse ||
    !filteredPaths?.length ||
    !templateVariables
  )
    return null;

  return (
    <StepCard.Popover
      icon={['far', 'exchange-alt']}
      tooltip={allMapped ? 'Edit mapping for this response' : 'Fields are missing mappings'}
    >
      <div className="scaleui scale-tailwind scale-theme p-4 space-y-4 max-h-96 overflow-scroll">
        {filteredPaths && templateVariables && (
          <ResponseTemplateStep
            key={`step-${step.params.id}-${stepIndex}-${candidateId}-${fieldId}`}
            responseIndex={stepIndex}
            title={`Step ${stepIndex} - ${step?.title}`}
            subtitle={step?.params.title ?? ''}
            paths={filteredPaths}
            response={stepTemplatedResponse}
            updateResponse={updateEditedResponse}
            variables={templateVariables}
            isPopper
            numberFieldScale={
              candidateId && numberFieldScale
                ? { ...numberFieldScale, scaleToDataBank: false }
                : undefined
            }
          />
        )}
        <Button variant="primary" onClick={saveTemplatedResponse}>
          Save
        </Button>
      </div>
    </StepCard.Popover>
  );
};
